import React, { useState, useCallback } from 'react';
import {
  fetchBacktest, fetchBacktestRange,
  fetchBacktestStraddle100, fetchBacktestStraddle100Range,
  fetchBacktestThetaExpiry, fetchBacktestThetaExpiryRange,
  fetchBacktestStraddle50Expiry, fetchBacktestStraddle50ExpiryRange,
  fetchBacktestThetaSensexWed, fetchBacktestThetaSensexWedRange,
  fetchBacktestStraddle300SensexWed, fetchBacktestStraddle300SensexWedRange,
  fetchBacktestThetaSensexExpiry, fetchBacktestThetaSensexExpiryRange,
  fetchBacktestStraddle100SensexExpiry, fetchBacktestStraddle100SensexExpiryRange,
  fetchStrategyMeta,
} from '../api';

const STRATEGY_BACKTEST_MAP = {
  Theta: { single: fetchBacktest, range: fetchBacktestRange },
  Straddle100: { single: fetchBacktestStraddle100, range: fetchBacktestStraddle100Range },
  ThetaExpiry: { single: fetchBacktestThetaExpiry, range: fetchBacktestThetaExpiryRange },
  Straddle50Expiry: { single: fetchBacktestStraddle50Expiry, range: fetchBacktestStraddle50ExpiryRange },
  ThetaSensexWed: { single: fetchBacktestThetaSensexWed, range: fetchBacktestThetaSensexWedRange },
  Straddle300SensexWed: { single: fetchBacktestStraddle300SensexWed, range: fetchBacktestStraddle300SensexWedRange },
  ThetaSensexExpiry: { single: fetchBacktestThetaSensexExpiry, range: fetchBacktestThetaSensexExpiryRange },
  Straddle100SensexExpiry: { single: fetchBacktestStraddle100SensexExpiry, range: fetchBacktestStraddle100SensexExpiryRange },
};

function getDefaultDate() {
  const d = new Date(); d.setDate(d.getDate() - 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

export default function Backtest() {
  const [selectedStrategy, setSelectedStrategy] = useState('Theta');
  const [fromDate, setFromDate] = useState(getDefaultDate);
  const [toDate, setToDate] = useState(getDefaultDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [meta, setMeta] = useState(null);

  // Load meta on first render
  React.useEffect(() => {
    fetchStrategyMeta().then(r => setMeta(r.data?.meta)).catch(() => {});
  }, []);

  const runBacktest = useCallback(async () => {
    setLoading(true);
    setError(null);
    setData(null);
    setExpandedDay(null);
    try {
      const fns = STRATEGY_BACKTEST_MAP[selectedStrategy];
      if (!fns) { setError('Unknown strategy'); setLoading(false); return; }
      let res;
      if (fromDate === toDate) {
        res = await fns.single(fromDate);
        if (res.data?.status === 'ok') {
          const s = res.data.summary;
          setData({
            status: 'ok',
            from_date: fromDate,
            to_date: toDate,
            lot_size: res.data.lot_size,
            aggregate: {
              trading_days: 1,
              winning_days: s.pnl_per_lot >= 0 ? 1 : 0,
              losing_days: s.pnl_per_lot < 0 ? 1 : 0,
              win_rate: s.pnl_per_lot >= 0 ? 100 : 0,
              total_trades: s.total_trades,
              total_adjustments: s.total_adjustments,
              pnl_per_lot: s.pnl_per_lot,
              pnl_total: s.pnl_total,
              avg_pnl_per_day: s.pnl_per_lot,
            },
            days: [{ date: res.data.date || fromDate, status: 'ok', summary: s, trades: res.data.trades, events: res.data.events }],
          });
        } else {
          setError(res.data?.message || 'Backtest failed');
        }
      } else {
        res = await fns.range(fromDate, toDate);
        if (res.data?.status === 'ok') {
          setData(res.data);
        } else {
          setError(res.data?.message || 'Backtest failed');
        }
      }
    } catch (err) {
      setError('Failed to run backtest');
    }
    setLoading(false);
  }, [selectedStrategy, fromDate, toDate]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Date', 'Open', 'Close', 'Range', 'Trades', 'Adj', 'P&L/Lot', 'P&L Total', 'Trade#', 'Strike', 'Entry', 'CE Sell', 'PE Sell', 'Exit', 'CE Buy', 'PE Buy', 'Reason', 'CE P&L', 'PE P&L', 'Total P&L']];
    data.days.forEach(day => {
      if (day.status !== 'ok') { rows.push([day.date, '', '', '', '', '', '', '', day.message || 'No data']); return; }
      const s = day.summary;
      if (day.trades?.length > 0) {
        day.trades.forEach((t, i) => {
          rows.push([
            i === 0 ? day.date : '', i === 0 ? s.nifty_open : '', i === 0 ? s.nifty_close : '',
            i === 0 ? s.nifty_range?.toFixed(0) : '', i === 0 ? s.total_trades : '',
            i === 0 ? s.total_adjustments : '', i === 0 ? s.pnl_per_lot?.toFixed(2) : '',
            i === 0 ? s.pnl_total : '',
            t.trade_no, t.strike, t.entry_time, t.ce_entry?.toFixed(2), t.pe_entry?.toFixed(2),
            t.exit_time, t.ce_exit?.toFixed(2), t.pe_exit?.toFixed(2), t.exit_reason,
            t.ce_pnl?.toFixed(2), t.pe_pnl?.toFixed(2), t.total_pnl?.toFixed(2),
          ]);
        });
      } else {
        rows.push([day.date, s.nifty_open, s.nifty_close, s.nifty_range?.toFixed(0), s.total_trades, s.total_adjustments, s.pnl_per_lot?.toFixed(2), s.pnl_total]);
      }
    });
    const a = data.aggregate;
    rows.push([]);
    rows.push(['SUMMARY', 'Days:', a.trading_days, 'WinRate:', a.win_rate + '%', 'Trades:', a.total_trades, '', 'P&L/Lot:', a.pnl_per_lot?.toFixed(2), 'Total:', a.pnl_total, 'Avg:', a.avg_pnl_per_day?.toFixed(2)]);
    const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `backtest_${selectedStrategy}_${data.from_date}_to_${data.to_date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const stratMeta = meta?.[selectedStrategy] || {};

  return (
    <div className="page-backtest">
      <h1 className="page-title">Backtest</h1>

      <div className="bt-controls-panel">
        <div className="bt-strategy-select">
          <label>Strategy</label>
          <select value={selectedStrategy} onChange={e => { setSelectedStrategy(e.target.value); setData(null); setError(null); }}>
            {Object.keys(STRATEGY_BACKTEST_MAP).map(s => (
              <option key={s} value={s}>{meta?.[s]?.display_name || s}</option>
            ))}
          </select>
        </div>
        {stratMeta.description && (
          <p className="bt-strategy-desc">{stratMeta.description}</p>
        )}
        <div className="bt-controls">
          <div className="bt-date-group">
            <label>From</label>
            <input type="date" className="bt-date-input" value={fromDate} onChange={e => setFromDate(e.target.value)} max={new Date().toISOString().split('T')[0]} />
          </div>
          <div className="bt-date-group">
            <label>To</label>
            <input type="date" className="bt-date-input" value={toDate} onChange={e => setToDate(e.target.value)} max={new Date().toISOString().split('T')[0]} />
          </div>
          <button className="bt-run-btn" onClick={runBacktest} disabled={loading}>
            {loading ? 'Running...' : 'Run Backtest'}
          </button>
          {data && <button className="bt-export-btn" onClick={exportCSV}>Export CSV</button>}
        </div>
      </div>

      {loading && (
        <div className="loading">
          <div className="spinner" />
          <p>Running {stratMeta.display_name || selectedStrategy} backtest... Each day takes ~30s.</p>
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}

      {data && (
        <div className="bt-results">
          {/* Aggregate Summary */}
          <div className="bt-summary">
            <div className="bt-summary-card">
              <span className="bt-label">Period</span>
              <span className="bt-val">{data.from_date} → {data.to_date}</span>
            </div>
            <div className="bt-summary-card highlight">
              <span className="bt-label">Trading Days</span>
              <span className="bt-val big">{data.aggregate.trading_days}</span>
            </div>
            <div className="bt-summary-card highlight pnl-pos">
              <span className="bt-label">Winning Days</span>
              <span className="bt-val big">{data.aggregate.winning_days}</span>
            </div>
            <div className="bt-summary-card highlight pnl-neg">
              <span className="bt-label">Losing Days</span>
              <span className="bt-val big">{data.aggregate.losing_days}</span>
            </div>
            <div className="bt-summary-card highlight">
              <span className="bt-label">Win Rate</span>
              <span className="bt-val big">{data.aggregate.win_rate}%</span>
            </div>
            <div className="bt-summary-card highlight">
              <span className="bt-label">Total Trades</span>
              <span className="bt-val big">{data.aggregate.total_trades}</span>
            </div>
            <div className={`bt-summary-card highlight ${data.aggregate.pnl_per_lot >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
              <span className="bt-label">Total P&L / Lot</span>
              <span className="bt-val big">{data.aggregate.pnl_per_lot >= 0 ? '+' : ''}{data.aggregate.pnl_per_lot?.toFixed(2)}</span>
            </div>
            <div className={`bt-summary-card highlight ${data.aggregate.pnl_total >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
              <span className="bt-label">Total P&L (₹)</span>
              <span className="bt-val big">₹{data.aggregate.pnl_total >= 0 ? '+' : ''}{data.aggregate.pnl_total?.toLocaleString('en-IN')}</span>
            </div>
            <div className={`bt-summary-card ${data.aggregate.avg_pnl_per_day >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
              <span className="bt-label">Avg P&L / Day</span>
              <span className="bt-val">{data.aggregate.avg_pnl_per_day >= 0 ? '+' : ''}{data.aggregate.avg_pnl_per_day?.toFixed(2)}</span>
            </div>
          </div>

          {/* Daily Breakdown */}
          <div className="bt-day-table">
            <h3>Daily Breakdown</h3>
            <div className="table-wrapper">
              <table className="bt-trade-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Open</th>
                    <th>Close</th>
                    <th>Range</th>
                    <th>Trades</th>
                    <th>Adj</th>
                    <th>P&L/Lot</th>
                    <th>P&L (₹)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((day) => (
                    <React.Fragment key={day.date}>
                      <tr
                        className={`day-row ${day.status !== 'ok' ? 'day-error' : day.summary?.pnl_per_lot >= 0 ? 'day-win' : 'day-loss'}`}
                        onClick={() => setExpandedDay(expandedDay === day.date ? null : day.date)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td><strong>{day.date}</strong></td>
                        {day.status === 'ok' ? (
                          <>
                            <td>{day.summary.nifty_open?.toLocaleString('en-IN')}</td>
                            <td>{day.summary.nifty_close?.toLocaleString('en-IN')}</td>
                            <td>{day.summary.nifty_range?.toFixed(0)}</td>
                            <td>{day.summary.total_trades}</td>
                            <td>{day.summary.total_adjustments}</td>
                            <td className={day.summary.pnl_per_lot >= 0 ? 'positive' : 'negative'}>
                              <strong>{day.summary.pnl_per_lot >= 0 ? '+' : ''}{day.summary.pnl_per_lot?.toFixed(2)}</strong>
                            </td>
                            <td className={day.summary.pnl_total >= 0 ? 'positive' : 'negative'}>
                              ₹{day.summary.pnl_total >= 0 ? '+' : ''}{day.summary.pnl_total?.toLocaleString('en-IN')}
                            </td>
                            <td className="expand-icon">{expandedDay === day.date ? '▼' : '▶'}</td>
                          </>
                        ) : (
                          <>
                            <td colSpan="6" className="day-error-msg">{day.message || 'No data'}</td>
                            <td></td>
                          </>
                        )}
                      </tr>
                      {expandedDay === day.date && day.status === 'ok' && (
                        <tr className="expanded-row">
                          <td colSpan="9">
                            <div className="bt-expanded-detail">
                              <table className="bt-trade-table inner-table">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Strike</th>
                                    <th>Entry</th>
                                    <th>CE Sell ₹</th>
                                    <th>PE Sell ₹</th>
                                    <th>Exit</th>
                                    <th>CE Buy ₹</th>
                                    <th>PE Buy ₹</th>
                                    <th>Reason</th>
                                    <th>CE P&L</th>
                                    <th>PE P&L</th>
                                    <th>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {day.trades?.map((t, i) => (
                                    <tr key={i}>
                                      <td>{t.trade_no}</td>
                                      <td className="strike-cell">{t.strike}</td>
                                      <td className="time-cell">{t.entry_time}</td>
                                      <td>{t.ce_entry?.toFixed(2)}</td>
                                      <td>{t.pe_entry?.toFixed(2)}</td>
                                      <td className="time-cell">{t.exit_time}</td>
                                      <td>{t.ce_exit?.toFixed(2)}</td>
                                      <td>{t.pe_exit?.toFixed(2)}</td>
                                      <td className="reason-cell">{t.exit_reason}</td>
                                      <td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>
                                        {t.ce_pnl >= 0 ? '+' : ''}{t.ce_pnl?.toFixed(2)}
                                      </td>
                                      <td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>
                                        {t.pe_pnl >= 0 ? '+' : ''}{t.pe_pnl?.toFixed(2)}
                                      </td>
                                      <td className={`total-cell ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>
                                        {t.total_pnl >= 0 ? '+' : ''}{t.total_pnl?.toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {data.days.filter(d => d.status === 'ok').length > 1 && (
                    <tr className="total-row">
                      <td><strong>TOTAL</strong></td>
                      <td colSpan="4"></td>
                      <td></td>
                      <td className={`total-cell ${data.aggregate.pnl_per_lot >= 0 ? 'positive' : 'negative'}`}>
                        <strong>{data.aggregate.pnl_per_lot >= 0 ? '+' : ''}{data.aggregate.pnl_per_lot?.toFixed(2)}</strong>
                      </td>
                      <td className={`total-cell ${data.aggregate.pnl_total >= 0 ? 'positive' : 'negative'}`}>
                        <strong>₹{data.aggregate.pnl_total >= 0 ? '+' : ''}{data.aggregate.pnl_total?.toLocaleString('en-IN')}</strong>
                      </td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
